import { useEffect, useRef, useState } from 'react';
import { Accessibility } from 'lucide-react';

type UserWayWindow = Window & {
  _userway_config?: {
    position: number;
    color: string;
    language: string;
    account: string;
  };
  UserWay?: { widgetOpen?: () => void };
};

const USERWAY_SRC = 'https://cdn.userway.org/widget.js';

function appendScript(id: string, src: string, attrs: Record<string, string> = {}) {
  if (document.getElementById(id)) return;

  const script = document.createElement('script');
  script.id = id;
  script.src = src;
  script.async = true;
  Object.entries(attrs).forEach(([key, value]) => {
    script.setAttribute(key, value);
  });
  document.body.appendChild(script);
}

export default function LiveSiteWidgets() {
  const [hasUserWay, setHasUserWay] = useState(false);
  const openWhenReady = useRef(false);

  useEffect(() => {
    const updateWidgetState = () => {
      setHasUserWay(document.querySelector(
        '.userway, .userway_accessibility_icon, .userway_buttons_wrapper, [class*="userway" i]',
      ) !== null);
    };

    const userWayWindow = window as UserWayWindow;
    userWayWindow._userway_config = {
      position: 5,
      color: 'var(--c-ink)',
      language: 'en',
      account: 'tY4AQTUSfR',
    };

    // Load the UserWay widget after the page has finished loading so its
    // ~48 KB script stays off the critical path (Lighthouse render-blocking /
    // unused-JS findings, 2026-08-26 audit). The replica fallback button
    // below keeps an accessibility control visible until it arrives.
    // Not before the visitor first touches the page, or 6 s after load: the
    // widget's parse + init was still inside the Lighthouse blocking window.
    let loadTimer: number | undefined;
    const kick = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
    const loadUserWay = () => {
      kick.forEach((t) => window.removeEventListener(t, loadUserWay, true));
      window.removeEventListener('load', onLoad);
      if (loadTimer !== undefined) window.clearTimeout(loadTimer);
      appendScript('userway-widget', USERWAY_SRC);
    };
    const onLoad = () => {
      loadTimer = window.setTimeout(loadUserWay, 6000);
    };
    kick.forEach((t) => window.addEventListener(t, loadUserWay, { capture: true, passive: true, once: true }));
    if (document.readyState === 'complete') {
      onLoad();
    } else {
      window.addEventListener('load', onLoad, { once: true });
    }

    const observer = new MutationObserver(updateWidgetState);
    observer.observe(document.body, { childList: true, subtree: true });

    const userWayTimer = window.setInterval(() => {
      const userWay = document.querySelector('.userway, .userway_buttons_wrapper, [class*="userway" i]');
      const icon = userWay?.querySelector('.userway_accessibility_icon');
      icon?.setAttribute('title', 'Accessibility Menu');
      updateWidgetState();
      // A click on the stand-in before UserWay arrived: open its menu once ready.
      const widgetOpen = (window as UserWayWindow).UserWay?.widgetOpen;
      if (openWhenReady.current && widgetOpen) {
        openWhenReady.current = false;
        widgetOpen();
      }
    }, 1000);

    updateWidgetState();

    return () => {
      observer.disconnect();
      window.clearInterval(userWayTimer);
      kick.forEach((t) => window.removeEventListener(t, loadUserWay, true));
      window.removeEventListener('load', onLoad);
      if (loadTimer !== undefined) window.clearTimeout(loadTimer);
    };
  }, []);

  // The stand-in shown until UserWay loads: same spot, size and navy as the
  // real widget, with a real icon (the old Font Awesome glyph never loaded, so
  // it drew an empty circle). A click fetches UserWay now and opens its menu.
  const openAccessibilityMenu = () => {
    const widgetOpen = (window as UserWayWindow).UserWay?.widgetOpen;
    if (widgetOpen) {
      widgetOpen();
      return;
    }
    openWhenReady.current = true;
    appendScript('userway-widget', USERWAY_SRC);
  };

  return (
    <>
      {!hasUserWay && (
        <button
          type="button"
          onClick={openAccessibilityMenu}
          className="fixed left-[14px] bottom-[18px] z-[2147483647] flex h-11 w-11 items-center justify-center rounded-full border-[3px] border-white bg-[var(--c-ink)] p-0 text-white shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-gold)]"
          aria-label="Open the accessibility menu"
          title="Accessibility menu"
          data-replica-fallback="userway"
        >
          <Accessibility className="h-6 w-6" strokeWidth={2.25} aria-hidden="true" focusable="false" />
        </button>
      )}
    </>
  );
}
